"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import AnimatedSection from "./AnimatedSection";
import Footer from "./Footer";

const VALUES = [
  {
    title: "Craft Over Convention",
    description:
      "I don't use templates. Every project is built from scratch to match the vision, the brand, and what the business actually needs. If it feels off-the-shelf, it's not done.",
    icon: "01",
  },
  {
    title: "Current Stack",
    description:
      "React, Next.js, TypeScript, Three.js — I use what works now because maintaining old code sucks. I don't chase every new framework, but I don't let the stack rot either.",
    icon: "02",
  },
  {
    title: "Design That Works",
    description:
      "Pretty isn't enough. The interface has to guide people where they need to go, with less friction, not more. I build things that are nice to look at and actually usable.",
    icon: "03",
  },
  {
    title: "Ship Fast, Fix Fast",
    description:
      "I move quickly, but I don't leave a mess behind. Tests where they matter, clean code where it counts, and enough documentation that I can come back to it in six months without swearing.",
    icon: "04",
  },
];

const AboutContent = () => {
  return (
    <main className="relative min-h-screen bg-[#070710]">
      <div className="hero-scanlines pointer-events-none fixed inset-0 z-10 opacity-30" />
      <nav className="fixed top-0 z-50 flex w-full items-center justify-between px-3 py-3 sm:px-6 sm:py-4 backdrop-blur-md bg-[#0A0A0F]/80 border-b border-white/[0.04]">
        <Link href="/" className="flex items-center gap-2 sm:gap-3 min-w-0">
          <div className="relative h-7 w-7 shrink-0 sm:h-8 sm:w-8">
            <Image
              src="/images/logo-dark.jpg"
              alt="MDG Labs"
              fill
              className="rounded-full object-cover"
            />
          </div>
          <span className="font-[family-name:var(--font-space-grotesk)] text-sm font-semibold">
            <span className="hero-mdg-glow">MDG</span>{" "}
            <span className="text-white/90">Labs</span>
          </span>
        </Link>
        <Link
          href="/"
          className="shrink-0 rounded-full bg-white/5 px-3 py-1.5 text-xs font-medium text-white/60 ring-1 ring-white/10 transition-all hover:bg-white/10 hover:text-white sm:px-4 sm:py-2"
        >
          <span className="sm:hidden">&larr; Home</span>
          <span className="hidden sm:inline">Back to Home</span>
        </Link>
      </nav>

      <section className="relative flex min-h-[60vh] w-full flex-col items-center justify-center overflow-hidden bg-[#070710] px-4 pt-24 pb-16">
        <div className="relative z-10 flex flex-col items-center text-center">
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="mb-8 text-sm font-medium uppercase tracking-[0.25em] text-emerald-400/80 font-[family-name:var(--font-jetbrains-mono)]"
          >
            <span className="text-emerald-500/60">{">"}</span> About Me
          </motion.p>

          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="hero-logo-pulse mb-6"
          >
            <div className="relative h-24 w-24 md:h-32 md:w-32 rounded-full overflow-hidden ring-2 ring-emerald-500/30">
              <Image
                src="/images/logo-dark.jpg"
                alt="MDG Labs — Circuit Tree Logo"
                fill
                className="rounded-full object-cover"
                loading="eager"
                fetchPriority="high"
                sizes="(min-width: 768px) 128px, 96px"
              />
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.7 }}
            className="hero-chromatic font-[family-name:var(--font-space-grotesk)] text-4xl font-bold tracking-tight md:text-6xl"
          >
            <span className="hero-mdg-glow">MDG</span>{" "}
            <span className="text-white/95">LABS</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.7 }}
            className="mt-5 max-w-xl text-base md:text-lg text-white/70 font-[family-name:var(--font-space-grotesk)]"
          >
            One person, a lot of caffeine, and a decent IDE.
          </motion.p>
        </div>
      </section>

      {/* Why I Do This */}
      <section className="relative z-20 px-4 py-24">
        <div className="mx-auto max-w-3xl">
          <AnimatedSection>
            <div className="glass-card rounded-2xl p-10 md:p-14">
              <p className="mb-3 text-sm font-medium uppercase tracking-[0.25em] text-emerald-400/80 font-[family-name:var(--font-jetbrains-mono)]">
                <span className="text-emerald-500/60">{">"}</span> Why I Do This
              </p>
              <h2 className="hero-chromatic font-[family-name:var(--font-space-grotesk)] text-2xl font-bold leading-relaxed md:text-3xl">
                I like building things that{" "}
                <span className="gradient-text">work well and look good.</span>
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-[#94A3B8]">
                MDG Labs is just me — Micah Gray — building stuff for people who need it. 
                I started because I was tired of seeing small businesses get sold overpriced 
                garbage by agencies that didn’t care if the site actually worked. 
                Now I build directly for the people using the product.
              </p>
              <p className="mt-4 text-lg leading-relaxed text-[#94A3B8]">
                The name is simple. <strong className="text-white/90">Labs</strong> because 
                I’m always tinkering — trying a new animation library, testing a different 
                database setup, breaking things and fixing them. I learn by building, and 
                I get better with every project.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Before The Code */}
      <section className="relative z-20 px-4 pt-8 pb-24">
        <div className="mx-auto max-w-3xl">
          <AnimatedSection>
            <div className="glass-card rounded-2xl p-10 md:p-14">
              <p className="mb-3 text-sm font-medium uppercase tracking-[0.25em] text-emerald-400/80 font-[family-name:var(--font-jetbrains-mono)]">
                <span className="text-emerald-500/60">{">"}</span> Before The Code
              </p>
              <h2 className="hero-chromatic font-[family-name:var(--font-space-grotesk)] text-2xl font-bold leading-relaxed md:text-3xl">
                Twenty years on jobsites taught me{" "}
                <span className="gradient-text">you can’t drywall before rough-in.</span>
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-[#94A3B8]">
                Mix of hands-on tradework and running jobs — boots on site to
                client walkthroughs, estimates to change orders to final punch
                lists. Two decades of one stubborn lesson: order of operations is
                everything. Sequence the work wrong and you’re tearing things
                out before you’re finishing them up.
              </p>
              <p className="mt-4 text-lg leading-relaxed text-[#94A3B8]">
                That instinct carries straight into software. I build features in
                the order that lets the next feature exist — schemas before flows,
                flows before UI, UI before polish. I scope tight, estimate honest,
                and don’t promise what the structure won’t hold.
              </p>
              <p className="mt-4 text-lg leading-relaxed text-[#94A3B8]">
                It’s also why BidFlow — used by contractors who pay for it —
                actually fits how a trade day works. DispatchIQ is in the build
                queue, close to finished, aimed at the same problem from a
                different angle. I’m not designing for a market I’ve
                read about. I’m building the tools I would have wanted in my
                own truck.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* My Approach */}
      <section className="relative z-20 px-4 py-24">
        <div className="section-divider mx-auto mb-20 max-w-xl" />

        <div className="mx-auto max-w-6xl">
          <AnimatedSection className="mb-16 text-center">
            <p className="mb-3 text-sm font-medium uppercase tracking-[0.25em] text-emerald-400/80 font-[family-name:var(--font-jetbrains-mono)]">
              <span className="text-emerald-500/60">{">"}</span> My Approach
            </p>
            <h2 className="hero-chromatic font-[family-name:var(--font-space-grotesk)] text-3xl font-bold md:text-5xl">
              How I <span className="gradient-text">work</span>
            </h2>
          </AnimatedSection>

          <div className="grid gap-8 md:grid-cols-2">
            {VALUES.map((value, index) => (
              <AnimatedSection key={value.title} delay={index * 0.1}>
                <motion.div
                  whileHover={{ y: -4 }}
                  className="glass-card group relative overflow-hidden rounded-2xl p-8"
                >
                  <span className="mb-4 inline-block font-[family-name:var(--font-jetbrains-mono)] text-3xl font-bold text-emerald-500/20">
                    {value.icon}
                  </span>
                  <h3 className="font-[family-name:var(--font-space-grotesk)] text-xl font-semibold text-white">
                    {value.title}
                  </h3>
                  <p className="mt-3 leading-relaxed text-[#94A3B8]">
                    {value.description}
                  </p>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* The Studio */}
      <section className="relative z-20 px-4 py-24">
        <div className="section-divider mx-auto mb-20 max-w-xl" />

        <div className="mx-auto max-w-4xl">
          <AnimatedSection className="text-center">
            <p className="mb-3 text-sm font-medium uppercase tracking-[0.25em] text-emerald-400/80 font-[family-name:var(--font-jetbrains-mono)]">
              <span className="text-emerald-500/60">{">"}</span> The Studio
            </p>
            <h2 className="hero-chromatic font-[family-name:var(--font-space-grotesk)] text-3xl font-bold md:text-5xl">
              One person. <span className="gradient-text">No handoffs.</span>
            </h2>
          </AnimatedSection>

          <AnimatedSection delay={0.2} className="mt-12">
            <div className="glass-card rounded-2xl p-10 md:p-14">
              <div className="flex flex-col items-center gap-8 md:flex-row md:items-start">
                <div className="relative h-24 w-24 shrink-0 rounded-2xl overflow-hidden ring-2 ring-emerald-500/20 glow-emerald">
                  <Image
                    src="/images/logo-forest.png"
                    alt="MDG Labs"
                    fill
                    className="object-cover"
                  />
                </div>

                <div>
                  <p className="text-lg leading-relaxed text-[#94A3B8]">
                    MDG Labs is a solo studio — me, Micah Gray, building lean,
                    focused, and obsessed with quality. I stay solo so every
                    client gets direct access to the engineer who actually builds
                    their product. No account managers. No hand-offs. No
                    telephone game.
                  </p>
                  <p className="mt-4 text-lg leading-relaxed text-[#94A3B8]">
                    When you work with MDG Labs, you’re working with the person
                    who writes the code, designs the interfaces, and deploys to
                    production. That’s how I deliver faster, iterate smarter,
                    and build products that actually work.
                  </p>
                  <p className="mt-4 text-lg leading-relaxed text-[#94A3B8]">
                    I work with the modern stack — current frameworks, cloud
                    infrastructure, and tools that let one person do the work of
                    three. I’m not trying to be a big agency. I’m trying to be
                    the best one-person shop you’ll ever hire.
                  </p>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-20 px-4 py-24">
        <div className="section-divider mx-auto mb-20 max-w-xl" />

        <AnimatedSection className="mx-auto max-w-2xl text-center">
          <p className="mb-3 text-sm font-medium uppercase tracking-[0.25em] text-emerald-400/80 font-[family-name:var(--font-jetbrains-mono)]">
            <span className="text-emerald-500/60">{">"}</span> Next Step
          </p>
          <h2 className="hero-chromatic font-[family-name:var(--font-space-grotesk)] text-3xl font-bold md:text-4xl">
            Got a project in mind?{" "}
            <span className="gradient-text">Let’s talk.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-[#94A3B8]">
            Whether you’re launching something new or fixing what you already have —
            I’m around.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <motion.a
              href="mailto:support@mdglabs.dev"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              className="group rounded-md border border-emerald-500/40 bg-emerald-950/30 px-5 py-3 text-sm font-medium text-emerald-300 font-[family-name:var(--font-jetbrains-mono)] tracking-wider backdrop-blur-sm transition-all hover:border-emerald-400/80 hover:bg-emerald-900/50 hover:text-emerald-200"
            >
              <span className="text-emerald-500/60 group-hover:text-emerald-400">[</span>
              {" START_CONVERSATION "}
              <span className="text-emerald-500/60 group-hover:text-emerald-400">]</span>
            </motion.a>
            <Link
              href="/#work"
              className="rounded-md border border-white/10 bg-white/5 px-5 py-3 text-sm font-medium text-white/70 font-[family-name:var(--font-jetbrains-mono)] tracking-wider transition-all hover:border-white/30 hover:text-white"
            >
              ./projects
            </Link>
          </div>
        </AnimatedSection>
      </section>

      <Footer />
    </main>
  );
};

export default AboutContent;
